import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { TextSkeleton } from 'src/components/shared/TextSkeleton'
import { RefreshMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import {
  getInvoiceDueInterest,
  getInvoiceDueInterestError,
  getInvoiceDueInterestLoading,
} from 'src/selectors/invoice'
import { useSelector } from 'react-redux'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'

interface Props {
  onRefresh(): void
}

/**
 * Retool shows this under the selected partner labelled "General Due Interest Left", which
 * reads as the customer's balance. It is not: `LoanService.getRepaymentDueInterest` takes a
 * partnerId, logs it, and then calls `oatfiService.getDueInterest()` — which takes no arguments
 * and hits GigSafe's own Oatfi product. The figure is identical for every customer, and the
 * caption says so.
 *
 * There is no repay button here: the only repay-due-interest route is per partner invoice and
 * loan-only, so that action lives on the detail-panel rows.
 */
const DueInterestCardComponent = ({ onRefresh }: Props) => {
  const dueInterest = useSelector(getInvoiceDueInterest)
  const isLoading = useSelector(getInvoiceDueInterestLoading)
  const error = useSelector(getInvoiceDueInterestError)

  const hasReading = dueInterest !== null && dueInterest.dueInterest !== undefined

  return (
    <section
      data-testid="invoices-due-interest-card"
      css={tw`mb-5 rounded-lg border border-solid border-shades-100 bg-shades-0 p-6`}
    >
      <div css={tw`flex flex-row items-start justify-between gap-4`}>
        <div>
          <Typography variant="h4" css={tw`mb-1 text-shades-900`}>
            Due interest
          </Typography>
          <Typography variant="body2" css={tw`text-shades-500`}>
            This is the GigSafe-wide Oatfi due-interest balance, not this customer&apos;s. The
            backend returns the same figure for every customer.
          </Typography>
        </div>
        <Button
          size="small"
          color="white"
          leftIcon={<RefreshMinor width={20} />}
          disabled={isLoading}
          onClick={onRefresh}
          data-testid="invoices-due-interest-refresh-button"
        >
          Refresh
        </Button>
      </div>

      <div css={tw`mt-4`}>
        {isLoading ? <TextSkeleton lines={2} dataTestId="invoices-due-interest-loading" /> : null}

        {/* Inline, not a toast: the grid beside it stays usable when only this call fails. */}
        {!isLoading && error ? (
          <Typography variant="body2" css={tw`text-critical-500`}>
            {error}
          </Typography>
        ) : null}

        {/* An empty state, never a fabricated $0.00 — the endpoint answers null when Oatfi
            reports nothing due, and a zero would state a balance it never reported. */}
        {!isLoading && !error && !hasReading ? (
          <Typography variant="body2" css={tw`text-shades-500`}>
            No due interest reported.
          </Typography>
        ) : null}

        {!isLoading && !error && hasReading ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="h2" data-testid="invoices-due-interest-amount">
              {formatCentsAsUsd(dueInterest?.dueInterest)}
            </Typography>
            {dueInterest?.loans !== undefined ? (
              <Typography variant="body2" css={tw`text-shades-500`}>
                Across {dueInterest.loans} {dueInterest.loans === 1 ? 'loan' : 'loans'}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}

export const DueInterestCard = memo(DueInterestCardComponent)
